import type { JSX } from "react";

/**
 * The password-visibility toggle glyphs for the sign-in card.
 * Ported verbatim from xop-edge-staff's `apps/web/src/pages/login/icons.tsx`
 * so both sign-in screens share one design; re-sync by re-copying that
 * file rather than hand-editing drift here. Decorative (`aria-hidden`) — the
 * toggle button carries the accessible label.
 */
export function EyeIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M1.5 10s3-6 8.5-6 8.5 6 8.5 6-3 6-8.5 6S1.5 10 1.5 10Z" />
      <circle cx="10" cy="10" r="2.5" />
    </svg>
  );
}

export function EyeOffIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 3l14 14" />
      <path d="M9.3 4.1A8.6 8.6 0 0 1 10 4c5.5 0 8.5 6 8.5 6a16 16 0 0 1-2.4 3.2" />
      <path d="M6.4 6.1C3.7 7.6 1.5 10 1.5 10s3 6 8.5 6c1.5 0 2.9-.4 4-1" />
      <path d="M7.7 7.7a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}

/* ── Footer contact glyphs ──────────────────────────────────────────────
 * The customer support portal draws these with `lucide-react`. This app
 * does not carry that dependency (and a sign-in footer is not worth one),
 * so the three glyphs it actually uses are inlined here at lucide's own
 * 24px grid / 1.5 stroke so the two footers still look like siblings.
 * All decorative — the surrounding link text carries the meaning. */

function Glyph({
  children,
  size = 14,
  opacity = 0.65,
}: {
  children: JSX.Element;
  size?: number;
  /** The footer glyphs are deliberately quieter than their link text; the
   * top bar's theme icons are the control itself and must not be. */
  opacity?: number;
}): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ flexShrink: 0, opacity }}
    >
      {children}
    </svg>
  );
}

export function MapPinIcon(): JSX.Element {
  return (
    <Glyph>
      <>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
      </>
    </Glyph>
  );
}

export function PhoneIcon(): JSX.Element {
  return (
    <Glyph>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />
    </Glyph>
  );
}

export function MailIcon(): JSX.Element {
  return (
    <Glyph>
      <>
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m2 7 10 6 10-6" />
      </>
    </Glyph>
  );
}

/* ── Theme-toggle glyphs ────────────────────────────────────────────────
 * The support portal's toggle draws lucide's `Sun` / `Moon`. Same 24px
 * grid, same 1.5–1.6 stroke, drawn here for the same "no dependency for
 * three glyphs" reason as the footer set above. Full opacity: unlike the
 * footer icons these ARE the control, not a decoration beside a label. */

export function SunIcon(): JSX.Element {
  return (
    <Glyph opacity={1}>
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </>
    </Glyph>
  );
}

export function MoonIcon(): JSX.Element {
  return (
    <Glyph opacity={1}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </Glyph>
  );
}
